import React, { useState, useRef, useEffect, useId, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Building2, MapPin, Clock, Shield, Users, Target, Zap, Factory, FlaskConical, CircleDollarSign, Globe, Edit, Clipboard } from 'lucide-react';
import { CDMOApplication } from '../lib/supabase';
import {
  buildCapabilitySections,
  buildEquipmentGroups,
  CDMO_WIDE_CAPTION,
  countEquipmentItems,
  countUniqueCapabilities,
} from '../utils/sponsorCardDetails';
import { useNavigate } from 'react-router-dom';
import StatusButton from './StatusButton';
import { toast } from 'react-hot-toast';
import { createPortal } from 'react-dom';
import FDAForm483Overlay from './FDAForm483Overlay';
import MatchScoreTooltip from './MatchScoreTooltip';
import GmpCertificationsTooltip from './GmpCertificationsTooltip';
import CardDetailAccordion from './common/CardDetailAccordion';
import { VALIDATION_MESSAGES } from '../constants/messages';
import { getPrimaryCapabilityLabel } from '../constants';
import { formatMatchPercentageForDisplay } from '../utils/formatMatchScoreDisplay';
import { computeAnchoredOverlayPosition } from '../utils/anchoredOverlayPosition';

interface ScoreBreakdown {
  productType: { earned: number; max: number; details?: string };
  detailedServices: { earned: number; max: number; details?: string };
  developmentStage: { earned: number; max: number; details?: string };
  supportType: { earned: number; max: number; details?: string };
  batchSize: { earned: number; max: number; details?: string };
  partnerType: { earned: number; max: number; details?: string };
  certifications: { earned: number; max: number; details?: string };
  speedToStart: { earned: number; max: number; details?: string };
  budget?: { earned: number; max: number; details?: string };
  operating_regions?: { earned: number; max: number; details?: string };
  startupFriendly: { earned: number; max: number; details?: string };
  otherDetails: { earned: number; max: number; details?: string };
  cdmoPartnerProfile: { earned: number; max: number; details?: string };
}

/** How the match badge is shown on the card (Close Alternatives use a qualitative label, not %). */
export type CDMOCardScoreDisplayMode = 'percentage' | 'strong_alternative';

/** Keys for long chip rows that support “+N more” / “Show less” (names mirror related `cdmo` fields). */
type ExpandableChipSectionKey =
  | 'primaryCapabilities'
  | 'targetClients'
  | 'batchSizes'
  | 'engagementScales';

interface CDMOCardProps {
  cdmo: CDMOApplication & {
    scoreBreakdown?: ScoreBreakdown;
  };
  percentageMatch?: number;
  /**
   * `strong_alternative` — hide the numeric % on the badge (tier-2 can still read 100% while tier-1 was relaxed).
   * Hover/click still opens the same “Why This Match?” breakdown when `scoreBreakdown` is present.
   */
  scoreDisplayMode?: CDMOCardScoreDisplayMode;
  onRequestIntroduction?: (cdmoId: string) => void;
  isRequestDisabled?: boolean;
  requestStatus?: string;
  introductionRequestId?: string;
  isAdmin?: boolean;
}

const CDMOCard: React.FC<CDMOCardProps> = ({
  cdmo,
  percentageMatch,
  scoreDisplayMode = 'percentage',
  onRequestIntroduction,
  isRequestDisabled = false,
  requestStatus,
  introductionRequestId,
  isAdmin = false
}) => {
  const navigate = useNavigate();
  const [isScoreTooltipOpen, setIsScoreTooltipOpen] = useState(false);
  const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom');
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [arrowLeft, setArrowLeft] = useState(20);
  const [isCertificationListOpen, setIsCertificationListOpen] = useState(false);
  const [certTooltipPos, setCertTooltipPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isFDAOverlayOpen, setIsFDAOverlayOpen] = useState(false);
  const [fdaPlacement, setFdaPlacement] = useState<'top' | 'bottom'>('bottom');
  const [fdaOverlayPos, setFdaOverlayPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [fdaArrowLeft, setFdaArrowLeft] = useState(20);
  const [expandedSections, setExpandedSections] = useState<
    Partial<Record<ExpandableChipSectionKey, boolean>>
  >({});

  /**
   * Toggles expanded vs collapsed chip lists (primary capabilities, clients, batch sizes, engagement scales).
   * @param key - Which section to toggle
   */
  const toggleExpandedChipSection = (key: ExpandableChipSectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /**
   * The card's blue capability chip. Shared by the Primary Modalities summary and the §6
   * Primary Capabilities accordion so the two cannot drift into different blues again — they
   * label the same kind of thing and read as one family.
   */
  const capabilityChipClass =
    'px-3 py-1 bg-electric-blue/10 text-deep-indigo text-xs font-medium rounded-lg border border-electric-blue/20';
  const chipExpandButtonClass =
    'px-3 py-1 bg-slate-gray/10 hover:bg-slate-gray/20 text-slate-gray hover:text-charcoal text-xs font-medium rounded-lg border border-slate-gray/30 cursor-pointer transition-all duration-200 hover:shadow-sm';
  /** §6 technical detail, derived once per card. See {@link ../utils/sponsorCardDetails}. */
  const capabilitySections = useMemo(() => buildCapabilitySections(cdmo), [cdmo]);
  const uniqueCapabilityCount = useMemo(() => countUniqueCapabilities(cdmo), [cdmo]);
  const equipmentGroups = useMemo(() => buildEquipmentGroups(cdmo), [cdmo]);
  const equipmentItemCount = useMemo(() => countEquipmentItems(equipmentGroups), [equipmentGroups]);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const tooltipTimeout = useRef<number | undefined>(undefined);
  const fdaTriggerRef = useRef<HTMLButtonElement | null>(null);
  const fdaOverlayRef = useRef<HTMLDivElement | null>(null);
  const fdaHoverTimeout = useRef<number | undefined>(undefined);
  const certTriggerRef = useRef<HTMLButtonElement | null>(null);
  const certTooltipRef = useRef<HTMLDivElement>(null);
  const certHoverTimeout = useRef<number | undefined>(undefined);
  /**
   * A tap fires a synthetic pointerenter/focus *before* click, which would open the panel and
   * leave the click to toggle it straight back shut — so touch users never saw it (§6.3). Recording
   * the pointer type lets hover and focus stay mouse/keyboard affordances and leaves the tap to the
   * click handler alone.
   */
  const certPointerType = useRef<string>('');
  /** True once a click has pinned the panel open, so hover-out and blur leave it alone. */
  const certPinned = useRef(false);
  const certTooltipId = useId();
  /** Integer % for badge styling and label; engine may pass precise floats. */
  const displayMatchPct =
    percentageMatch !== undefined
      ? formatMatchPercentageForDisplay(Number(percentageMatch))
      : undefined;

  const showStrongAlternativeBadge = scoreDisplayMode === 'strong_alternative';
  const showMatchScoreBadge = showStrongAlternativeBadge || displayMatchPct !== undefined;

  const openTooltip = () => {
    if (tooltipTimeout.current) window.clearTimeout(tooltipTimeout.current);
    setIsScoreTooltipOpen(true);
  };

  const openFDAOverlay = () => {
    if (fdaHoverTimeout.current) window.clearTimeout(fdaHoverTimeout.current);
    setIsFDAOverlayOpen(true);
  };

  const closeFDAOverlay = () => {
    fdaHoverTimeout.current = window.setTimeout(() => setIsFDAOverlayOpen(false), 150);
  };

  const closeTooltip = () => {
    tooltipTimeout.current = window.setTimeout(() => setIsScoreTooltipOpen(false), 150);
  };

  const openCertificationList = () => {
    if (certHoverTimeout.current) window.clearTimeout(certHoverTimeout.current);
    setIsCertificationListOpen(true);
  };

  /**
   * Delayed so the pointer can reach the portalled panel, which is not a child of the trigger.
   * A pinned panel ignores this entirely — see {@link toggleCertificationList}.
   */
  const closeCertificationList = () => {
    if (certPinned.current) return;
    if (certHoverTimeout.current) window.clearTimeout(certHoverTimeout.current);
    certHoverTimeout.current = window.setTimeout(() => setIsCertificationListOpen(false), 150);
  };

  /** Unpin and close now, for Escape and for a click anywhere outside. */
  const dismissCertificationList = () => {
    certPinned.current = false;
    if (certHoverTimeout.current) window.clearTimeout(certHoverTimeout.current);
    setIsCertificationListOpen(false);
  };

  /**
   * A click pins the panel open rather than toggling it shut. On desktop a click is always
   * preceded by a hover that already opened it, so a plain toggle meant clicking could only ever
   * *hide* the detail — the opposite of what §6.3 asks a click to do. Pinned, it survives the
   * pointer leaving, and a second click dismisses it.
   */
  const toggleCertificationList = () => {
    if (certPinned.current) {
      dismissCertificationList();
      return;
    }
    certPinned.current = true;
    openCertificationList();
  };

  const updateTooltipPosition = () => {
    const el = triggerRef.current;
    const tip = tooltipRef.current;
    if (!el || !tip) return;

    const r = el.getBoundingClientRect();
    const tipW = tip.offsetWidth;
    const tipH = tip.offsetHeight;
    const margin = 10;

    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const placeTop = spaceBelow < tipH + margin && spaceAbove > spaceBelow;

    const top = placeTop ? r.top - tipH - margin : r.bottom + margin;

    let left = r.right - tipW;
    left = Math.max(8, Math.min(left, window.innerWidth - tipW - 8));

    const anchorCenter = r.left + r.width / 2;
    let arrow = anchorCenter - left;
    arrow = Math.max(12, Math.min(arrow, tipW - 12));

    setPlacement(placeTop ? 'top' : 'bottom');
    setTooltipPos({ top, left });
    setArrowLeft(arrow);
  };

  const updateFDAOverlayPosition = () => {
    const el = fdaTriggerRef.current;
    const overlay = fdaOverlayRef.current;
    if (!el || !overlay) return;

    const r = el.getBoundingClientRect();
    const overlayW = overlay.offsetWidth;
    const overlayH = overlay.offsetHeight;
    const margin = 10;

    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const placeTop = spaceBelow < overlayH + margin && spaceAbove > spaceBelow;

    const top = placeTop ? r.top - overlayH - margin : r.bottom + margin;

    let left = r.right - overlayW;
    left = Math.max(8, Math.min(left, window.innerWidth - overlayW - 8));

    const anchorCenter = r.left + r.width / 2;
    let arrow = anchorCenter - left;
    arrow = Math.max(12, Math.min(arrow, overlayW - 12));

    setFdaPlacement(placeTop ? 'top' : 'bottom');
    setFdaOverlayPos({ top, left });
    setFdaArrowLeft(arrow);
  };

  const updateCertTooltipPosition = () => {
    const trigger = certTriggerRef.current;
    const tip = certTooltipRef.current;
    if (!trigger || !tip) return;

    const { top, left } = computeAnchoredOverlayPosition(
      trigger.getBoundingClientRect(),
      tip.offsetWidth,
      tip.offsetHeight,
      { width: window.innerWidth, height: window.innerHeight }
    );
    setCertTooltipPos({ top, left });
  };

  useEffect(() => () => {
    if (certHoverTimeout.current) window.clearTimeout(certHoverTimeout.current);
  }, []);

  useEffect(() => {
    if (!isCertificationListOpen) return;
    const raf = requestAnimationFrame(updateCertTooltipPosition);
    const onScrollOrResize = () => updateCertTooltipPosition();
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissCertificationList();
    };
    // A pinned panel has to be dismissable without going back to the trigger.
    const onOutsidePointerDown = (e: Event) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (certTriggerRef.current?.contains(target)) return;
      if (certTooltipRef.current?.contains(target)) return;
      dismissCertificationList();
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('keydown', onEsc);
    document.addEventListener('pointerdown', onOutsidePointerDown);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('keydown', onEsc);
      document.removeEventListener('pointerdown', onOutsidePointerDown);
    };
  }, [isCertificationListOpen]);

  useEffect(() => {
    if (!isScoreTooltipOpen) return;
    const raf = requestAnimationFrame(updateTooltipPosition);
    const onScrollOrResize = () => updateTooltipPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isScoreTooltipOpen]);

  useEffect(() => {
    if (!isFDAOverlayOpen) return;
    const raf = requestAnimationFrame(updateFDAOverlayPosition);
    const onScrollOrResize = () => updateFDAOverlayPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isFDAOverlayOpen]);

  const handleRequestIntroduction = () => {
    if (onRequestIntroduction && cdmo.id) {
      onRequestIntroduction(cdmo.id);
    }
  };

  const handleGiveFeedback = () => {
    if (introductionRequestId) {
      navigate(`/review/${introductionRequestId}/startup`);
    }
  };

  const handleEditCDMO = () => {
    if (cdmo.id) {
      navigate(`/cdmo-application?edit=${cdmo.id}`);
    }
  };

  const handleCopyClaimLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (cdmo.admin_claim_token) {
      const idParam = cdmo.id ? `&id=${cdmo.id}` : '';
      const claimLink = `${window.location.origin}/startup-signup?token=${cdmo.admin_claim_token}${idParam}`;
      navigator.clipboard.writeText(claimLink).then(() => {
        toast.success(VALIDATION_MESSAGES.CLAIM_LINK_COPIED);
      }).catch((err) => {
        console.error('Failed to copy: ', err);
        toast.error(VALIDATION_MESSAGES.CLAIM_LINK_COPY_FAILED);
      });
    } else {
      toast.error(VALIDATION_MESSAGES.NO_ADMIN_CLAIM_TOKEN);
    }
  };



  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50 overflow-hidden relative"
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center flex-1">
            <div className="w-12 h-12 shrink-0 bg-gradient-primary rounded-xl flex items-center justify-center mr-4 shadow-lg">
              <Building2 className="w-6 h-6 shrink-0 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-charcoal mb-1">{cdmo.company_name}</h3>
              <div className="relative mb-1.5 flex justify-start">
                <button
                  ref={fdaTriggerRef}
                  type="button"
                  onMouseEnter={openFDAOverlay}
                  onMouseLeave={closeFDAOverlay}
                  onClick={() => setIsFDAOverlayOpen(v => !v)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer flex items-center gap-1 bg-electric-blue text-white border border-electric-blue shadow-sm hover:bg-deep-indigo hover:border-deep-indigo transition-colors"
                >
                  View FDA 483s →
                </button>
              </div>
              <p className="text-sm text-slate-gray">{cdmo.contact_name}</p>
            </div>
          </div>

          {/* Match Score and Status Tags */}
          <div className="flex flex-col gap-2 items-end flex-shrink-0">
            {/* Copy link (admin) – in flow so it doesn't overlap Request Pending */}
            {isAdmin && cdmo.admin_claim_token && (
              <button
                type="button"
                onClick={handleCopyClaimLink}
                title="Copy claim link"
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-slate-gray hover:text-electric-blue hover:bg-white/80 border border-transparent hover:border-slate-gray/20 transition-all duration-200 text-xs font-medium"
                aria-label="Copy claim link"
              >
                <Clipboard className="w-4 h-4 flex-shrink-0" />
                <span>Copy link</span>
              </button>
            )}
            {/* Request Status Badge */}
            {requestStatus && (
              <div className={`px-3 py-1 rounded-lg text-xs font-bold ${requestStatus === 'accepted'
                ? 'bg-green-100 text-green-700 border border-green-200'
                : requestStatus === 'pending'
                  ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                  : requestStatus === 'declined'
                    ? 'bg-red-100 text-red-700 border border-red-200'
                    : 'bg-gray-100 text-gray-700 border border-gray-200'
                }`}>
                {requestStatus === 'accepted' ? 'Request Accepted' :
                  requestStatus === 'pending' ? 'Request Pending' :
                    requestStatus === 'declined' ? 'Request Declined' : requestStatus}
              </div>
            )}

            {/* Match Score with Tooltip */}
            {/* <div className="flex items-center gap-2 mt-1"> */}
            {showMatchScoreBadge && (
              <div className="relative">
                <button
                  ref={triggerRef}
                  type="button"
                  onMouseEnter={openTooltip}
                  onMouseLeave={closeTooltip}
                  onClick={() => setIsScoreTooltipOpen(v => !v)}
                  aria-expanded={isScoreTooltipOpen}
                  aria-controls={cdmo.scoreBreakdown ? `score-breakdown-${cdmo.id || ''}` : undefined}
                  className={
                    showStrongAlternativeBadge
                      ? 'px-3 py-1 rounded-lg text-xs font-bold cursor-pointer flex items-center text-center leading-tight bg-amber-50 text-amber-950 border border-amber-200 hover:bg-amber-100 max-w-[9.5rem]'
                      : `px-3 py-1 rounded-lg text-sm font-bold cursor-pointer flex items-center ${
                          (displayMatchPct ?? 0) >= 80
                            ? 'bg-lime-green/20 text-lime-green border border-lime-green/30'
                            : (displayMatchPct ?? 0) >= 60
                              ? 'bg-electric-blue/20 text-electric-blue border border-electric-blue/30'
                              : (displayMatchPct ?? 0) >= 40
                                ? 'bg-soft-teal/20 text-soft-teal border border-soft-teal/30'
                                : 'bg-slate-gray/20 text-slate-gray border border-slate-gray/30'
                        }`
                  }
                  aria-label={
                    showStrongAlternativeBadge
                      ? 'Strong alternative — open why this match details'
                      : `${displayMatchPct}% match — open why this match details`
                  }
                >
                  {showStrongAlternativeBadge ? (
                    <>
                      Strong Alternative
                      <Target className="w-3 h-3 ml-1 shrink-0 opacity-70" aria-hidden />
                    </>
                  ) : (
                    <>
                      {displayMatchPct}% Match
                      <Target className="w-3 h-3 ml-1 opacity-60" aria-hidden />
                    </>
                  )}
                </button>

                {cdmo.scoreBreakdown &&
                  createPortal(
                    <MatchScoreTooltip
                      isOpen={isScoreTooltipOpen}
                      onMouseEnter={openTooltip}
                      onMouseLeave={closeTooltip}
                      onClose={() => setIsScoreTooltipOpen(false)}
                      position={tooltipPos}
                      arrowLeft={arrowLeft}
                      placement={placement}
                      scoreBreakdown={cdmo.scoreBreakdown}
                      cdmoId={cdmo.id}
                      contentRef={tooltipRef}
                    />,
                    document.body
                  )}
              </div>
            )}

            {/* </div> */}
            {/* Status Tags */}
            <div className="flex flex-col gap-1">
              {cdmo?.startup_friendly && (
                <span className="px-2 py-1 bg-lime-green/10 text-lime-green text-xs font-medium rounded-lg border border-lime-green/20">
                  Startup Friendly
                </span>
              )}
              {cdmo?.willingness_feasibility_studies && (
                <span className="px-2 py-1 bg-electric-blue/10 text-electric-blue text-xs font-medium rounded-lg border border-electric-blue/20">
                  Feasibility Studies
                </span>
              )}
            </div>
          </div>
        </div>


        {/* Location (Legacy) */}
        {cdmo.facility_locations_details && (
          <div className="flex items-center mb-4">
            <MapPin className="w-4 h-4 text-slate-gray mr-2" />
            <p className="text-sm text-slate-gray line-clamp-2">{cdmo.facility_locations_details}</p>
          </div>
        )}

        {/* Primary Modalities */}
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <FlaskConical className="w-4 h-4 text-electric-blue mr-2" />
            <span className="text-sm font-medium text-charcoal">Primary Modalities</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {cdmo.primary_capabilities?.slice(0, expandedSections.primaryCapabilities ? undefined : 3).map((capability, index) => (
              <span
                key={index}
                className={capabilityChipClass}
              >
                {getPrimaryCapabilityLabel(capability)}
              </span>
            ))}
            {cdmo.primary_capabilities && cdmo.primary_capabilities.length > 3 && (
              <button
                type="button"
                onClick={() => toggleExpandedChipSection('primaryCapabilities')}
                className={chipExpandButtonClass}
              >
                {expandedSections.primaryCapabilities
                  ? 'Show less'
                  : `+${cdmo.primary_capabilities.length - 3} more`}
              </button>
            )}
          </div>
        </div>

        {/* Target Client Types */}
        {cdmo.target_client_types && cdmo.target_client_types.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <Users className="w-4 h-4 text-soft-teal mr-2" />
              <span className="text-sm font-medium text-charcoal">Target Clients</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {cdmo.target_client_types.slice(0, expandedSections.targetClients ? undefined : 2).map((clientType, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-soft-teal/10 text-soft-teal text-xs font-medium rounded-lg border border-soft-teal/20"
                >
                  {clientType}
                </span>
              ))}
              {cdmo.target_client_types.length > 2 && (
                <button
                  type="button"
                  onClick={() => toggleExpandedChipSection('targetClients')}
                  className={chipExpandButtonClass}
                >
                  {expandedSections.targetClients
                    ? 'Show less'
                    : `+${cdmo.target_client_types.length - 2} more`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Batch Sizes */}
        {cdmo.typical_batch_sizes && cdmo.typical_batch_sizes.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <Target className="w-4 h-4 text-lime-green mr-2" />
              <span className="text-sm font-medium text-charcoal">Batch Sizes</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {cdmo.typical_batch_sizes.slice(0, expandedSections.batchSizes ? undefined : 2).map((batchSize, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-lime-green/10 text-lime-green text-xs font-medium rounded-lg border border-lime-green/20"
                >
                  {batchSize}
                </span>
              ))}
              {cdmo.typical_batch_sizes.length > 2 && (
                <button
                  type="button"
                  onClick={() => toggleExpandedChipSection('batchSizes')}
                  className={chipExpandButtonClass}
                >
                  {expandedSections.batchSizes
                    ? 'Show less'
                    : `+${cdmo.typical_batch_sizes.length - 2} more`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Project Kickoff & Regulatory / GMP */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {cdmo.speed_to_start && (
            <div className="flex items-center">
              <Clock className="w-4 h-4 text-electric-blue mr-2" />
              <div>
                <p className="text-xs text-slate-gray">Project Kickoff</p>
                <p className="text-sm font-medium text-charcoal">{cdmo.speed_to_start}</p>
              </div>
            </div>
          )}



          {cdmo.gmp_certifications && cdmo.gmp_certifications.length > 0 && (
            <div className="flex items-center">
              <Shield className="w-4 h-4 text-lime-green mr-2" />
              <div>
                <p className="text-xs text-slate-gray">Regulatory / GMP</p>
                <button
                  ref={certTriggerRef}
                  type="button"
                  onPointerDown={(e) => {
                    certPointerType.current = e.pointerType;
                  }}
                  onPointerEnter={(e) => {
                    if (e.pointerType === 'mouse') openCertificationList();
                  }}
                  onPointerLeave={(e) => {
                    if (e.pointerType === 'mouse') closeCertificationList();
                  }}
                  onFocus={() => {
                    if (certPointerType.current !== 'touch') openCertificationList();
                  }}
                  onBlur={() => {
                    // Cleared on the way out so the recorded type only ever describes the
                    // interaction that just ended. Left set, a single earlier tap would suppress
                    // every later keyboard focus on this button — Tab on a hybrid tablet would
                    // silently stop opening the panel for the rest of the session.
                    certPointerType.current = '';
                    closeCertificationList();
                  }}
                  onClick={toggleCertificationList}
                  aria-expanded={isCertificationListOpen}
                  aria-describedby={isCertificationListOpen ? certTooltipId : undefined}
                  className="text-sm font-medium text-charcoal underline decoration-dotted decoration-slate-gray/60 underline-offset-2 cursor-pointer"
                >
                  {cdmo.gmp_certifications.length} requirement{cdmo.gmp_certifications.length !== 1 ? 's' : ''}
                </button>
                {isCertificationListOpen &&
                  createPortal(
                    <GmpCertificationsTooltip
                      certifications={cdmo.gmp_certifications}
                      position={certTooltipPos}
                      contentRef={certTooltipRef}
                      id={certTooltipId}
                      onMouseEnter={openCertificationList}
                      onMouseLeave={closeCertificationList}
                    />,
                    document.body
                  )}
              </div>
            </div>
          )}
        </div>
        {/* Budget & Regions */}
        <div className="flex flex-col gap-2 mb-4">
          {/* Budget */}
          {(cdmo?.budget_ranges?.length ?? 0) > 0 && (
            <div className="flex items-center text-sm">
              <CircleDollarSign className="w-4 h-4 text-lime-green mr-2 flex-shrink-0" />
              <span className="font-medium text-charcoal mr-1">Typical Project Budget:</span>
              <span className="text-lime-green font-medium">
                {cdmo?.budget_ranges?.length >= 6 ? 'Flexible' : cdmo?.budget_ranges?.join(', ')}
              </span>
            </div>
          )}

          {/* Regions */}
          {(cdmo?.operating_regions?.length ?? 0) > 0 && (
            <div className="flex items-center text-sm">
              <Globe className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
              <span className="font-medium text-charcoal mr-1">Regions:</span>
              <span className="text-slate-gray">
                {cdmo.operating_regions.join(', ')}
              </span>
            </div>
          )}
        </div>

        {/* Engagement Scales */}
        {cdmo.engagement_scales && cdmo.engagement_scales.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center mb-2">
              <Zap className="w-4 h-4 text-gradient-pink-violet-start mr-2" />
              <span className="text-sm font-medium text-charcoal">Development Stages</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {cdmo.engagement_scales.slice(0, expandedSections.engagementScales ? undefined : 3).map((scale, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gradient-pink-violet-start/10 text-gradient-pink-violet-start text-xs font-medium rounded-lg border border-gradient-pink-violet-start/20"
                >
                  {scale}
                </span>
              ))}
              {cdmo.engagement_scales.length > 3 && (
                <button
                  type="button"
                  onClick={() => toggleExpandedChipSection('engagementScales')}
                  className={chipExpandButtonClass}
                >
                  {expandedSections.engagementScales
                    ? 'Show less'
                    : `+${cdmo.engagement_scales.length - 3} more`}
                </button>
              )}
            </div>
          </div>
        )}



        {/* Technical detail (§6) — below the summary fields, above Request Introduction. Everything
            here is stored CDMO data; nothing is inferred from a broader capability. */}
        {(capabilitySections.length > 0 || equipmentGroups.length > 0) && (
          <div className="flex flex-col gap-2 mb-5 pt-4 border-t border-slate-gray/15">
            {capabilitySections.length > 0 && (
              <CardDetailAccordion
                icon={<FlaskConical className="w-4 h-4 text-electric-blue" />}
                title="Primary Capabilities"
                summary={`· ${uniqueCapabilityCount}`}
              >
                {capabilitySections.map((section) => (
                  <div
                    key={section.facilityLabel ?? 'by-modality'}
                    className="pt-4 pb-4 first:pt-1 last:pb-0 [&+&]:border-t [&+&]:border-slate-gray/15"
                  >
                    {/* Only the facility heading is bold — modality headings stay plain, so the
                        facility boundaries are what the eye picks up first. */}
                    {section.facilityLabel && (
                      <p
                        className={`text-xs font-bold tracking-wide uppercase ${
                          section.isCdmoWide ? 'text-slate-gray' : 'text-charcoal'
                        }`}
                      >
                        {section.facilityLabel}
                      </p>
                    )}
                    {section.isCdmoWide && (
                      <p className="text-[10px] text-slate-gray mt-0.5">{CDMO_WIDE_CAPTION}</p>
                    )}
                    {section.groups.map((group) => (
                      <div key={group.modality}>
                        <p className="text-[10px] font-medium tracking-wide uppercase text-slate-gray mt-2.5 mb-1.5">
                          {getPrimaryCapabilityLabel(group.modality)}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {group.capabilities.map((capability) => (
                            <span
                              key={capability}
                              className={capabilityChipClass}
                            >
                              {capability}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </CardDetailAccordion>
            )}

            {/* Hidden entirely when the CDMO has recorded no equipment — never an empty state. */}
            {equipmentGroups.length > 0 && (
              <CardDetailAccordion
                icon={<Factory className="w-4 h-4 text-lime-green" />}
                title="Manufacturing Equipment"
                summary={`· ${equipmentItemCount} item${equipmentItemCount === 1 ? '' : 's'} · ${equipmentGroups.length} facilit${equipmentGroups.length === 1 ? 'y' : 'ies'}`}
              >
                {equipmentGroups.map((group) => (
                  <div key={group.facilityLabel} className="pt-3 first:pt-1">
                    <p className="text-xs font-bold tracking-wide uppercase text-charcoal mb-1.5">
                      {group.facilityLabel}
                    </p>
                    <ul className="flex flex-col gap-1">
                      {group.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-xs text-charcoal">
                          <span
                            className="mt-1.5 w-1 h-1 shrink-0 rounded-full bg-lime-green"
                            aria-hidden="true"
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardDetailAccordion>
            )}
          </div>
        )}
        {/* Update the FDA Overlay with hover handlers */}
        {createPortal(
          <FDAForm483Overlay
            companyName={cdmo.company_name}
            feiNumber={cdmo?.fei_number || null}
            isOpen={isFDAOverlayOpen}
            onClose={() => setIsFDAOverlayOpen(false)}
            onMouseEnter={openFDAOverlay}
            onMouseLeave={closeFDAOverlay}
            position={fdaOverlayPos}
            arrowLeft={fdaArrowLeft}
            placement={fdaPlacement}
            contentRef={fdaOverlayRef}
          />,
          document.body
        )}


        {/* Action Button */}
        <div className="space-y-3">
          <StatusButton
            requestStatus={requestStatus as 'accepted' | 'declined' | 'pending' | null}
            isRequestDisabled={isRequestDisabled}
            handleGiveFeedback={handleGiveFeedback}
            handleRequestIntroduction={handleRequestIntroduction}
            onRequestIntroduction={!!onRequestIntroduction}
          />

          {isAdmin && (
            <div className="flex flex-col gap-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleEditCDMO}
                className="w-full py-3 bg-gradient-primary text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Manage
              </motion.button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default CDMOCard;